import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, X, Power, PowerOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    preco: '',
    categoria: '',
    tags: '',
    nivel_importancia: 5,
    tag_destaque: false,
    ativo: true,
    imagens: []
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [showInactive]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const url = showInactive ? `${API}/products?include_inactive=true` : `${API}/products?include_inactive=true`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const handleToggleProduct = async (productId) => {
    try {
      const response = await axios.put(`${API}/products/${productId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao alterar status do produto');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await axios.post(`${API}/products/upload-images`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        imagens: [...prev.imagens, ...response.data.imagens]
      }));
      toast.success(`${files.length} imagem(ns) carregada(s)!`);
    } catch (error) {
      toast.error('Erro ao fazer upload das imagens');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      imagens: prev.imagens.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        preco: parseFloat(formData.preco),
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        url_imagem: formData.imagens[0] || 'https://via.placeholder.com/400'
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produto atualizado!');
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produto criado!');
      }

      setShowForm(false);
      setEditingProduct(null);
      setFormData({
        nome: '',
        descricao: '',
        preco: '',
        categoria: '',
        tags: '',
        nivel_importancia: 5,
        tag_destaque: false,
        imagens: []
      });
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      nome: product.nome,
      descricao: product.descricao,
      preco: product.preco.toString(),
      categoria: product.categoria,
      tags: product.tags.join(', '),
      nivel_importancia: product.nivel_importancia || 5,
      tag_destaque: product.tag_destaque || false,
      imagens: product.imagens || []
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Deseja realmente excluir este produto?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produto excluído!');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  return (
    <div>
      {!showForm ? (
        <>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Produtos</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                Mostrar inativos
              </label>
            </div>
            <Button onClick={() => setShowForm(true)} style={{ alignSelf: 'flex-start' }} data-testid="add-product-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Produto
            </Button>
          </div>

          {/* Mobile Cards View */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {products.map(product => (
              <div 
                key={product.id} 
                style={{ 
                  backgroundColor: product.ativo ? '#fff' : '#f5f5f5', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  opacity: product.ativo ? 1 : 0.7
                }}
                data-testid={`product-row-${product.id}`}
              >
                <div style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem' }}>
                  <div style={{ position: 'relative' }}>
                    <img 
                      src={product.imagens?.[0] || product.url_imagem} 
                      alt={product.nome} 
                      style={{ 
                        width: '80px', 
                        height: '80px', 
                        objectFit: 'cover', 
                        borderRadius: '6px',
                        flexShrink: 0,
                        filter: product.ativo ? 'none' : 'grayscale(50%)'
                      }} 
                    />
                    {!product.ativo && (
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        left: '4px',
                        backgroundColor: '#ef4444',
                        color: '#fff',
                        fontSize: '0.6rem',
                        padding: '2px 4px',
                        borderRadius: '2px'
                      }}>
                        INATIVO
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ 
                      fontWeight: '600', 
                      fontSize: '0.9375rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: product.ativo ? '#000' : '#666'
                    }}>
                      {product.nome}
                    </h3>
                    <p style={{ 
                      fontSize: '1rem', 
                      fontWeight: 'bold',
                      color: product.ativo ? '#000' : '#888',
                      marginTop: '0.25rem'
                    }}>
                      R$ {product.preco.toFixed(2)}
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      marginTop: '0.375rem',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        color: '#666',
                        backgroundColor: '#f5f5f5',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '4px'
                      }}>
                        {product.categoria}
                      </span>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        backgroundColor: '#e0e0e0',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '4px'
                      }}>
                        Nível: {product.nivel_importancia || 5}
                      </span>
                      {product.tag_destaque && (
                        <span style={{ 
                          backgroundColor: '#ffd700', 
                          padding: '0.125rem 0.375rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem' 
                        }}>
                          ⭐
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  padding: '0.5rem 0.75rem',
                  borderTop: '1px solid #eee',
                  backgroundColor: product.ativo ? '#fafafa' : '#f0f0f0'
                }}>
                  <Button 
                    variant={product.ativo ? "outline" : "default"}
                    size="sm" 
                    onClick={() => handleToggleProduct(product.id)}
                    style={{ 
                      backgroundColor: product.ativo ? 'transparent' : '#22c55e',
                      color: product.ativo ? '#666' : '#fff',
                      borderColor: product.ativo ? '#ddd' : '#22c55e'
                    }}
                    data-testid={`toggle-product-${product.id}`}
                  >
                    {product.ativo ? <PowerOff className="w-4 h-4 mr-1" /> : <Power className="w-4 h-4 mr-1" />}
                    {product.ativo ? 'Desativar' : 'Ativar'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(product)}
                    style={{ flex: 1 }}
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDelete(product.id)}
                    style={{ color: '#ef4444' }}
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{editingProduct ? 'Editar Produto' : 'Novo Produto'}</h3>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditingProduct(null); }}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input 
                id="nome" 
                data-testid="product-name-input"
                value={formData.nome} 
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })} 
                required 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <Label htmlFor="preco">Preço *</Label>
                <Input 
                  id="preco" 
                  type="number" 
                  step="0.01" 
                  data-testid="product-price-input"
                  value={formData.preco} 
                  onChange={(e) => setFormData({ ...formData, preco: e.target.value })} 
                  required 
                />
              </div>
              <div>
                <Label htmlFor="categoria">Categoria *</Label>
                <select 
                  id="categoria" 
                  data-testid="product-category-select"
                  value={formData.categoria} 
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })} 
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }} 
                  required
                >
                  <option value="">Selecione...</option>
                  {categories.map(cat => <option key={cat.id} value={cat.nome}>{cat.nome}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="descricao">Descrição *</Label>
              <Textarea 
                id="descricao" 
                data-testid="product-description-input"
                value={formData.descricao} 
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} 
                rows={3} 
                required 
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags</Label>
              <Input 
                id="tags" 
                data-testid="product-tags-input"
                value={formData.tags} 
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })} 
                placeholder="Separadas por vírgula" 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'end' }}>
              <div>
                <Label htmlFor="nivel_importancia">Nível de Importância: {formData.nivel_importancia}</Label>
                <input 
                  type="range" 
                  id="nivel_importancia" 
                  min="1" 
                  max="10" 
                  data-testid="product-importance-input"
                  value={formData.nivel_importancia} 
                  onChange={(e) => setFormData({ ...formData, nivel_importancia: parseInt(e.target.value) })} 
                  style={{ width: '100%', marginTop: '0.5rem' }} 
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                  <span>1 (Menor)</span>
                  <span>10 (Maior)</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '1.25rem' }}>
                <input 
                  type="checkbox" 
                  id="tag_destaque" 
                  data-testid="product-destaque-checkbox"
                  checked={formData.tag_destaque} 
                  onChange={(e) => setFormData({ ...formData, tag_destaque: e.target.checked })} 
                />
                <Label htmlFor="tag_destaque" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Destaque ⭐</Label>
              </div>
            </div>

            <div>
              <Label>Imagens do Produto</Label>
              <div 
                style={{ 
                  marginTop: '0.5rem', 
                  border: '2px dashed #ddd', 
                  borderRadius: '8px', 
                  padding: '1.5rem', 
                  textAlign: 'center', 
                  cursor: 'pointer' 
                }} 
                onClick={() => document.getElementById('image-upload').click()}
              >
                <Upload size={36} style={{ margin: '0 auto', color: '#999' }} />
                <p style={{ marginTop: '0.75rem', color: '#666', fontSize: '0.875rem' }}>Clique para fazer upload</p>
                <p style={{ fontSize: '0.75rem', color: '#999' }}>Múltiplas imagens permitidas</p>
                <input 
                  id="image-upload" 
                  type="file" 
                  multiple 
                  accept="image/*" 
                  data-testid="product-images-input"
                  onChange={handleImageUpload} 
                  style={{ display: 'none' }} 
                  disabled={uploading} 
                />
              </div>

              {uploading && (
                <p style={{ textAlign: 'center', marginTop: '0.75rem', color: '#666', fontSize: '0.875rem' }}>
                  Fazendo upload e comprimindo...
                </p>
              )}

              {formData.imagens.length > 0 && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
                  gap: '0.75rem', 
                  marginTop: '1rem' 
                }}>
                  {formData.imagens.map((img, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        position: 'relative', 
                        aspectRatio: '1/1', 
                        borderRadius: '6px', 
                        overflow: 'hidden', 
                        border: '2px solid #ddd' 
                      }}
                    >
                      <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button 
                        type="button" 
                        onClick={() => removeImage(index)} 
                        style={{ 
                          position: 'absolute', 
                          top: '2px', 
                          right: '2px', 
                          backgroundColor: 'rgba(255,255,255,0.9)', 
                          border: 'none', 
                          borderRadius: '50%', 
                          width: '20px', 
                          height: '20px', 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}
                        data-testid={`remove-image-${index}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { setShowForm(false); setEditingProduct(null); }}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={uploading} data-testid="save-product-button">
                {editingProduct ? 'Atualizar' : 'Criar'} Produto
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;